import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { first } from 'lodash';
import { bindActionCreators } from 'redux';

import feBookingCleanUpNewClient from 'actions/booking/feBookingCleanUpNewClient.action';
import feChangePaymentType from 'actions/payment/feChangePaymentType.action';
import beInitReaderPayment from 'actions/pos/beInitReaderPayment.action';
import feSetPosIntentId from 'actions/pos/feSetPosIntentId.action';
import ICO_FAILED from 'assets/images/dialogs/PaymentFailed.svg';
import IMG_VOUCHER from 'assets/images/icons/gift-purple.svg';
import determinePaymentType from 'bl/payment/determinePaymentType.bl';
import determineAndStorePaymentIntentId from 'bl/pos/determineAndStorePaymentIntentId.bl';
import determinePriceLines from 'bl/pos/determinePriceLines.bl';
import DialogMessage from 'components/dialog/DialogMessage.component';
import DepositSpinner from 'components/generic/DepositSpinner.component';
import Space from 'components/generic/Space.component';
import PosReaderDialog from 'components/pos/PosReaderDialog.component';
import PRDS from 'components/pos/PosReaderDialogStatus.const';
import PosReaderPriceLine from 'components/pos/PosReaderPriceLine.component';
import PosStateMessage from 'components/pos/PosStateMessage.component';
import CurrencySign from 'components/price/CurrencySign.component';
import { USE_OLD_CARD_PAYMENT_ERROR } from 'config/use.conf';
import EMSG from 'constants/messages/errorMessage.const';
import PAYMENT_ORIGIN_POS from 'constants/payment/paymentOriginPos.const';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCheckoutData, selectCheckoutPaymentOption, selectCheckoutPos } from 'selectors/checkout.selectors';
import { selectCurrentSalonId, selectCurrentSalonPosFlow } from 'selectors/currentSalon.selectors';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import fromAnyToCoinNum from 'utils/converters/any/fromAnyToCoinNum.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import fixed from 'utils/values/fixed.util';

import { createVoucher } from 'cashup/actions';
import { cancelChargeSlickCardOnline, cleanTransactionData } from 'checkout/actions';
import AlertDialog from 'checkout/components/AlertDialog';
import ConfirmationDialogFromCheckout from 'checkout/components/ConfirmationDialog';
import PayPopUpPriceLine from 'checkout/components/PayPopUpPriceLine.component';

const CTAG = 'PosVoucher';
const L = logger(CTAG);

const MARGIN_TOP_STYLE = Object.freeze({ marginTop: '-196px' });

const extractPaymentInfo = L.wrap('extractPaymentInfo()', (props) => {
  const o = asObject(props);
  const d = asObject(o.data);

  return {
    salonId: o.salonId,
    appointmentId: first(o.appointments),
    clientId: getAnyOrVoid('0.id', d.clients),
    chargeCard: d.onlineAmount > 0,
    cardNumberEnd: fromAnyToTrimmed(d.active_card),
    cardAmount: d.cardAmount,
    cashAmount: d.partCash,
    otherAmount: d.otherAmount,
    onlineAmount: d.onlineAmount,
    total: d.total,
    items: [{ description: '', price: 0, quantity: 0 }],
  };
});

const PosVoucher = (props) => {
  const {
    beInitReaderPayment,
    cancelChargeSlickCardOnline,
    cardOnFileChargeSuccess,
    chargingCardError,
    checkoutOption,
    cleanTransactionData,
    createVoucher,
    data,
    feBookingCleanUpNewClient,
    feChangePaymentType,
    feSetPosIntentId,
    onCloseVoucherView,
    pos,
    posFlow,
    salonId,
    setOpenClosePosModal,
    transaction,
    voucherObj,
  } = props;
  const [disableDuplicateTransaction, setDisableDuplicateTransaction] = useStateWhileMounted(false);
  const [errorMessageFromState, setErrorMessageFromState] = useStateWhileMounted('');
  const [confirmUpdateDialog, setConfirmUpdateDialog] = useStateWhileMounted(false);
  const [posStep, setPosStep] = useStateWhileMounted(null);
  const { t } = useTranslation();
  const { val: isDepositLoaderVisible, yay: showLoader, nay: hideLoader } = useTip(false);
  const { val: isConfirmDisabled, yay: disableConfirm, nay: enableConfirm } = useTip(false);

  const initTransaction = useCallback(
    async (paymentIntentId) => {
      const FTAG = 'initTransaction()';

      const { isFetching } = asObject(transaction);
      const cardAmount = fromAnyToCoinNum(voucherObj.amount);
      const onlineAmount = null;

      const isPosEnabled = posFlow === true; // NOTE: make sure POS is used only if posFLow is exactly true
      const paymentType = determinePaymentType({ checkoutOption, cardAmount, onlineAmount, isPosEnabled });

      if (isFetching || disableDuplicateTransaction) {
        return false;
      }
      setDisableDuplicateTransaction(true);

      // NOTE: depositListData might be set each cycle near the end of the big loop and is a shared mutable state

      feChangePaymentType({ paymentType });
      // TODO: determine why this needs updated after both for cycles finish

      try {
        setErrorMessageFromState(S.empty);

        const result = await createVoucher(salonId, { ...voucherObj, intent_id: paymentIntentId });

        setErrorMessageFromState(result.is.ok ? S.empty : t(EMSG.txCreateTransKey));

        return result.is.ok;
      } catch (error) {
        L.warn(FTAG, error);

        enableConfirm();

        hideLoader();

        setErrorMessageFromState(t(EMSG.txCreateTransKey));

        throw error;
      }
    },
    [
      checkoutOption,
      createVoucher,
      disableDuplicateTransaction,
      enableConfirm,
      feChangePaymentType,
      hideLoader,
      posFlow,
      salonId,
      setDisableDuplicateTransaction,
      setErrorMessageFromState,
      t,
      transaction,
      voucherObj,
    ],
  );

  const completeTransaction = useCallback(() => {
    hideLoader();
    feBookingCleanUpNewClient();
    setOpenClosePosModal(false);
  }, [feBookingCleanUpNewClient, hideLoader, setOpenClosePosModal]);

  const performTransaction = useCallback(
    async (paymentIntentId) => {
      // NOTE: on abort, initTransaction will return false, and on error it will simply throw it
      const success = await initTransaction(paymentIntentId);
      return success && completeTransaction();
    },
    [completeTransaction, initTransaction],
  );

  const confirm = useCallback(
    async (paymentIntentId) => {
      const FTAG = 'confirm()';

      try {
        await performTransaction(paymentIntentId);
      } catch (e) {
        L.warn(FTAG, 'performTransaction error:', e);
      }
      setErrorMessageFromState(S.empty);
    },
    [performTransaction, setErrorMessageFromState],
  );

  const closePopup = useCallback(() => {
    const { isFetching } = asObject(transaction);

    if (isFetching) {
      return;
    }

    if (posStep === PRDS.disconnected) {
      setPosStep(PRDS.closed);
      setOpenClosePosModal(false);
      onCloseVoucherView();
      return;
    }

    setOpenClosePosModal(false);
    onCloseVoucherView();
  }, [onCloseVoucherView, posStep, setOpenClosePosModal, setPosStep, transaction]);

  const handleConfirm = useCallback(() => {
    const { onlineAmount } = data;
    const isPosTransaction = posFlow === true; // NOTE: make sure POS is used only if posFLow is exactly true
    if (onlineAmount > 0) {
      return void setConfirmUpdateDialog(true);
    }

    if (!isPosTransaction || posStep === PRDS.success || posStep === PRDS.continue) {
      return void L.unignore('handleConfirm()', 'confirm:', confirm());
    }

    if (!posStep || posStep === PRDS.closed) {
      return void setPosStep(PRDS.open);
    }

    return void 1;
  }, [confirm, data, posFlow, posStep, setConfirmUpdateDialog, setPosStep]);

  const handleCancel = useCallback(() => {
    const { onlineAmount, cardAmount } = asObject(data);

    closePopup();

    if (onlineAmount > 0 || cardAmount > 0) {
      cancelChargeSlickCardOnline();
    }
  }, [cancelChargeSlickCardOnline, closePopup, data]);

  const handlePosReaderDone = useCallback(
    (options) => {
      const { canceled, success, paymentIntentId: idFromOptions } = asObject(options);

      if (success) {
        L.unignore('handlePosReaderDone()', 'confirm:', confirm(idFromOptions));
      }

      if (canceled) {
        handleCancel();
      }
    },
    [confirm, handleCancel],
  );

  const handlePosReaderChange = useCallback(
    (ev) => {
      if (ev?.status === PRDS.success) {
        showLoader();
        disableConfirm();
      }
      setPosStep(ev?.status);
    },
    [disableConfirm, setPosStep, showLoader],
  );

  const handlePosReaderAction = useCallback(
    (options) =>
      determineAndStorePaymentIntentId({
        readerId: asObject(options).readerId,
        pos,
        clientId: asObject(voucherObj).purchased_by_clientId,
        salonId,
        items: [
          {
            description: 'Voucher Sale',
            amount: fromAnyToCoinNum(asObject(voucherObj).amount),
            price: fromAnyToCoinNum(asObject(voucherObj).amount),
            quantity: 1,
          },
        ],
        paymentOrigin: PAYMENT_ORIGIN_POS.voucher,
        amount: asObject(voucherObj).amount,
        beInitReaderPayment,
        feSetPosIntentId,
      }),
    [beInitReaderPayment, voucherObj, feSetPosIntentId, pos, salonId],
  );

  const errorCallback = useCallback(() => {
    setErrorMessageFromState(S.empty);
    cleanTransactionData();
    closePopup();
  }, [cleanTransactionData, closePopup, setErrorMessageFromState]);

  const cancelPayment = useCallback(() => {
    setConfirmUpdateDialog(false);
  }, [setConfirmUpdateDialog]);

  const { isFetching, errorMessage: errorMessageFromTransaction } = asObject(transaction);

  const { cardNumberEnd, onlineAmount } = extractPaymentInfo(props);

  const isInPersonDialogOpen = USE_OLD_CARD_PAYMENT_ERROR && chargingCardError && cardOnFileChargeSuccess === false;
  // NOTE: allows for more customized error message to take precedance
  // SEE: https://getslick.atlassian.net/browse/GS-10878
  const errorMessage = isInPersonDialogOpen ? S.empty : errorMessageFromState || errorMessageFromTransaction;

  // NOTE: make sure POS is used only if posFLow is exactly true
  const isPosDialogOpen = posFlow === true && posStep && posStep !== PRDS.closed && posStep !== PRDS.cancel;

  return (
    <div data-bem="PosVoucher">
      {errorMessage && <AlertDialog title={errorMessage} buttonText={t('common.buttons.close')} onClick={errorCallback} onClose={errorCallback} />}

      {isDepositLoaderVisible && <DepositSpinner color="#3e0b6c" message={t('scheduler.voucher.finishCreation')} />}

      <PosReaderDialog open={isPosDialogOpen} onChange={handlePosReaderChange} onAction={handlePosReaderAction} onDone={handlePosReaderDone}>
        <DialogMessage>
          <PosStateMessage posStep={posStep} price={voucherObj.amount} />
        </DialogMessage>

        {determinePriceLines({ data, t }).map(({ finalPrice, clientId, productId, serviceId, ...extra }) => (
          <PosReaderPriceLine {...extra} total={finalPrice} key={`${clientId}-${serviceId}-${productId}`} />
        ))}

        <PosReaderPriceLine description={t('scheduler.voucher.total')} total={voucherObj.amount} />
      </PosReaderDialog>

      {isInPersonDialogOpen && (
        <ConfirmationDialogFromCheckout onRequestClose={handleCancel} title=" " leftButtonText="" rightButtonText={t('common.buttons.ok')} onRight={handleCancel} displayHeader="header-non">
          <div className="dialog-inner-title">{t('scheduler.voucher.chargeFailed')}</div>
          <img src={ICO_FAILED} alt="Client paid" className="dialog-image" />
          <div className="no-show-message center">{t(EMSG.chargeInPersonTransKey)}</div>
        </ConfirmationDialogFromCheckout>
      )}

      {confirmUpdateDialog && (
        <ConfirmationDialogFromCheckout
          title=""
          leftButtonText={t('common.buttons.cancel')}
          showHeader={false}
          onLeft={cancelPayment}
          rightButtonText={t('common.buttons.confirm')}
          onRight={() => void L.unignore('render()', 'confirm:', confirm())}
          onCancel={cancelPayment}
        >
          <div className="titelText">
            <Space>{t('scheduler.voucher.titlePart1')}</Space>
            <Space>
              <CurrencySign value={fixed(fromAnyToNumber(onlineAmount))} />
            </Space>
            <Space>{t('scheduler.voucher.titlePart2')}</Space>
          </div>
          <div className="violetText">
            <Space>{t('scheduler.voucher.titlePart3')}</Space>
            <Space>{fromAnyToTrimmed(cardNumberEnd)}</Space>
          </div>

          <div className="redText">{t('scheduler.voucher.note')}</div>
        </ConfirmationDialogFromCheckout>
      )}

      {!isConfirmDisabled && !isDepositLoaderVisible && (
        <div data-bem="PosVoucher__bill" className="chk-popup animated zoomIn pay-by-modal" style={MARGIN_TOP_STYLE}>
          <div data-bem="PosVoucher__title" className="chk-popup-header">
            <h2>
              {t('scheduler.voucher.confirmMessage')} <CurrencySign value={fromAnyToCoinNum(voucherObj.amount)} />
            </h2>
          </div>

          <div data-bem="PosVoucher__body" className="chk-popup-wrap">
            {voucherObj && <PayPopUpPriceLine description={t('scheduler.voucher.voucherLine')} icon={IMG_VOUCHER} value={fromAnyToNumber(voucherObj.amount)} />}
          </div>

          <div data-bem="PosVoucher__buttons" className="chk-popup-footer cfix">
            <button data-bem="PosVoucher__cancel-button" className="btn btn-violet-link" onClick={handleCancel}>
              {t('common.buttons.cancel')}
            </button>
            {isFetching ? (
              <button data-bem="PosVoucher__loading-button" className="btn btn-violet-link">
                {t('common.loading')}
              </button>
            ) : (
              <button data-bem="PosVoucher__confirm-button" id="confirm" className="btn btn-violet" onClick={handleConfirm} disabled={isConfirmDisabled}>
                {t('common.buttons.confirm')}
              </button>
            )}
          </div>
        </div>
      )}

      {!isDepositLoaderVisible && <div className="bg" />}
    </div>
  );
};

const mapStateToProps = (state) => ({
  cardOnFileChargeSuccess: state.checkout.cardOnFileChargeSuccess,
  chargingCardError: state.checkout.chargingCardError,
  checkoutOption: selectCheckoutPaymentOption(state),
  data: selectCheckoutData(state),
  pos: selectCheckoutPos(state),
  posFlow: selectCurrentSalonPosFlow(state),
  salonId: selectCurrentSalonId(state),
  transaction: state.transaction.create,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      beInitReaderPayment,
      createVoucher,
      feBookingCleanUpNewClient,
      cancelChargeSlickCardOnline,
      cleanTransactionData,
      feChangePaymentType,
      feSetPosIntentId,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(PosVoucher);
